import React, { createContext, useContext, useReducer } from "react";
import { useParams } from "react-router-dom";
import { reportReducer, initialReportState } from "./reportReducer";
import {
  formatReportUsers,
  formatReportRelativeFields,
  formatInvestigationInvolveds,
  formatInvestigationResources,
} from "./reportHelpers";
import { Outlet } from "react-router";

export const ReportContext = createContext();

export const ReportProvider = () => {
  const [state, dispatch] = useReducer(reportReducer, initialReportState);

  // Equivalente a startSettingDetailReport
  const setDetailReport = (reportDetail, usersAssigned) => {
    const { usersInfo, meInfo } = formatReportUsers(usersAssigned?.users || []);
    const relativeFields = formatReportRelativeFields(reportDetail);
    dispatch({
      type: "SET_CURRENT_REPORT",
      payload: {
        reportDetail,
        users: usersInfo,
        me: meInfo,
        relativeFields: relativeFields || {},
      },
    });
  };

  // Equivalente a startSettingInvestigation
  const setInvestigationContext = (investigation) => {
    const involveds = formatInvestigationInvolveds(investigation);
    const humanResources = formatInvestigationResources(investigation);
    dispatch({
      type: "SET_CURRENT_REPORT_INVESTIGATION",
      payload: {
        id: investigation.alias,
        name: investigation.title,
        ...involveds,
        ...humanResources,
      },
    });
  };

  const resetReport = () => dispatch({ type: "RESET_REPORT" });

  return (
    <ReportContext.Provider
      value={{ state, setDetailReport, setInvestigationContext, resetReport }}
    >
      <Outlet />
    </ReportContext.Provider>
  );
};

export const useReport = () => {
  const context = useContext(ReportContext);
  if (!context)
    throw new Error("useReport must be used within a ReportProvider");
  return context;
};

/**
 * Identificador del reporte actual (report_id) como string.
 *
 * La ruta /reports/tc/:id trae un tracking code, no el id del reporte, así que
 * el parámetro de la URL no sirve para los endpoints que piden report_id. El
 * detalle del reporte (que sí acepta id o tracking code) resuelve el id real y
 * queda en el contexto; este hook lo expone y sólo cae al parámetro de la URL
 * mientras el detalle aún no se ha cargado (ruta /reports/:id, donde el
 * parámetro ya es el id).
 */
export const useReportId = () => {
  const context = useContext(ReportContext);
  const { id } = useParams();
  const resolvedId = context?.state?.currentReport?.reportDetail?.id;
  // Se devuelve como string para conservar el mismo tipo que traía useParams()
  // y no cambiar los payloads que ya se enviaban desde /reports/:id.
  return resolvedId != null ? String(resolvedId) : id;
};
